import cx from "classnames";
import {
  LetterCloseIcon,
  LinkIcon,
  PencilIcon,
  PrinterIcon,
  EyeIcon,
} from "assets";
import { NotificationToast, SCREEN_WIDTH_ENUM } from "component-library";
import { useTranslation } from "i18n";
import ProfileButton from "pages/Profile/components/common/ProfileButton";
import ThemeToggle from "pages/Profile/components/ProfileHeader/components/ThemeToggle";
import { useDynamicWindowWidthFor } from "shared-hooks";
import { useIsUserEditable } from "pages/Profile/hooks/useIsUserEditable";
import { usePrintDataIsReady } from "pages/Profile/hooks/usePrintDataIsReady";
import { heapTrackingForMainPagePrintProfile } from "app/helpers/heapTracking";
import { runBrowserPrint } from "pages/Profile/helpers/runBrowserPrint";
import { dataTestIds } from "data-testids";

export interface ToastState {
  isOpen: boolean;
  message: string;
}

interface HeaderActionsProps {
  isEdit?: boolean;
  toggleEdit?: () => void;
  toastState: ToastState;
  onHideToast: () => void;
  onCopyLink: () => void;
  onOpenEmailPopup?: () => void;
}

const HeaderActions = ({
  isEdit,
  toggleEdit,
  toastState,
  onHideToast,
  onCopyLink,
  onOpenEmailPopup,
}: HeaderActionsProps) => {
  const { t } = useTranslation();
  const isUserEditable = useIsUserEditable();
  const isSmall = useDynamicWindowWidthFor(SCREEN_WIDTH_ENUM.SM);
  const printDataIsReady = usePrintDataIsReady();

  const handlePrint = () => {
    runBrowserPrint();
    heapTrackingForMainPagePrintProfile();
  };

  return (
    <>
      {toastState.isOpen && (
        <NotificationToast
          className="absolute left-1/2 top-10 z-[999] w-[20rem] -translate-x-1/2 transform rounded-[.5rem] text-[.8125rem] font-normal"
          show={toastState.isOpen}
          text={toastState.message}
          handleHide={onHideToast}
        />
      )}
      <div className="flex-end flex items-center justify-end gap-2">
        {isUserEditable && (
          <>
            <ProfileButton
              isText={!isSmall}
              onClick={toggleEdit}
              className={cx("py-2", {
                "bg-color-brand-one text-white hover:bg-color-brand-one/90":
                  isEdit,
              })}
              data-testid={dataTestIds.profile["Profile.EditMode.enableButton"]}
              aria-label={
                isEdit
                  ? t("common:aria.label.switchToPublicProfileView")
                  : t("common:aria.label.switchToEditProfileView")
              }
            >
              {isEdit ? (
                <EyeIcon
                  width={22}
                  height={22}
                  aria-hidden="true"
                  focusable="false"
                />
              ) : (
                <PencilIcon
                  width={22}
                  height={22}
                  aria-hidden="true"
                  focusable="false"
                />
              )}
              {!isSmall && (
                <span className="hidden md:block">
                  {isEdit
                    ? t("performer:profile.header.button.profileType.public")
                    : t("performer:profile.header.button.profileType.edit")}
                </span>
              )}
            </ProfileButton>
            <ProfileButton
              disabled={!printDataIsReady}
              onClick={handlePrint}
              data-testid={
                dataTestIds.profile["Profile.Header.PrintProfileButton"]
              }
              aria-label={t("common:aria.label.printProfile")}
            >
              <PrinterIcon aria-hidden="true" focusable="false" />
            </ProfileButton>
            <ProfileButton
              aria-label={t("common:aria.label.emailProfile")}
              onClick={() => onOpenEmailPopup?.()}
            >
              <LetterCloseIcon aria-hidden="true" focusable="false" />
            </ProfileButton>
            <ProfileButton
              aria-label={t("common:aria.label.copyProfileLink")}
              onClick={onCopyLink}
            >
              <LinkIcon aria-hidden="true" focusable="false" />
            </ProfileButton>
          </>
        )}
        <ThemeToggle />
      </div>
    </>
  );
};

export default HeaderActions;
